import { Box, Circle, Flex, Text, VStack } from "@chakra-ui/react"

interface AspectRow {
  label: string
  areaSqm: number
}

interface RoofOrientationVisualProps {
  aspectRows: AspectRow[]
}

const colorMap: Record<string, string> = {
  North: "gray.300",
  Northeast: "blue.300",
  East: "green.300",
  Southeast: "yellow.300",
  South: "orange.400",
  Southwest: "yellow.300",
  West: "green.300",
  Northwest: "blue.300"
}

const labelMap: Record<string, string> = {
  North: "North",
  Northeast: "NE",
  East: "East",
  Southeast: "SE",
  South: "South",
  Southwest: "SW",
  West: "West",
  Northwest: "NW"
}

// Compass order starting from North
const compassOrder = ["North", "Northeast", "East", "Southeast", "South", "Southwest", "West", "Northwest"]

const RoofOrientationVisual = ({ aspectRows }: RoofOrientationVisualProps) => {
  const flatRow = aspectRows.find((r) => r.label === "Flat")
  const compassRows = compassOrder
    .map((dir) => aspectRows.find((r) => r.label === dir))
    .filter(Boolean) as AspectRow[]

  const orientations = compassRows.map((row) => ({
    key: row.label.toLowerCase(),
    label: labelMap[row.label] || row.label,
    value: row.areaSqm,
    color: colorMap[row.label] || "gray.300"
  }))

  const maxArea = Math.max(...orientations.map((o) => o.value))
  const totalArea = orientations.reduce((sum, o) => sum + o.value, 0)

  return (
    <VStack gap={4} align="stretch">
      <Flex
        justify="center"
        align="center"
        position="relative"
        width="300px"
        height="300px"
        mx="auto"
      >
        {/* Compass Circle */}
        <Circle
          size="280px"
          border="2px"
          borderColor="gray.200"
          position="relative"
        >
          {/* Center text */}
          <VStack
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            align="center"
            gap={1}
          >
            <Text fontSize="lg" fontWeight="bold" color="gray.700">
              TOTAL:
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="gray.700">
              {totalArea.toFixed(1)}{" "}
              <Text as="span" fontSize="xs" color="gray.600">
                m²
              </Text>
            </Text>
          </VStack>

          {/* Orientation sectors */}
          {orientations.map((orientation, index) => {
            const angle = index * 45 - 90 // Start from North (-90 degrees)
            const radius = 120
            const x = Math.cos((angle * Math.PI) / 180) * radius
            const y = Math.sin((angle * Math.PI) / 180) * radius
            const size =
              maxArea > 0
                ? Math.max(20, (orientation.value / maxArea) * 60)
                : 20

            return (
              <Box
                key={orientation.key}
                position="absolute"
                top="50%"
                left="50%"
                transform={`translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`}
                textAlign="center"
              >
                <Circle
                  size={`${size}px`}
                  bg={orientation.value > 0 ? orientation.color : "gray.100"}
                  border="2px"
                  borderColor="white"
                  boxShadow="sm"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  _hover={{ transform: "scale(1.1)" }}
                  transition="all 0.2s"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color={orientation.value > 0 ? "gray.800" : "gray.400"}
                  >
                    {orientation.label}
                  </Text>
                </Circle>
                {orientation.value > 0 && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {orientation.value.toFixed(1)} m²
                  </Text>
                )}
              </Box>
            )
          })}
        </Circle>
      </Flex>

      <Text fontSize="sm" color="gray.600" textAlign="center" mt={2}>
       + Total Flat roof area: <strong>{flatRow ? flatRow.areaSqm.toFixed(1) : 'N/A'} m²</strong>
      </Text>
    </VStack>
  )
}

export default RoofOrientationVisual
